import { modifier, module } from 'constant'
import { useEvent, useState } from 'hooks'
import { blockBuilderSelector } from 'selectors'
import { cn } from 'utils'

declare global {
	interface Window {
		addNewBlockLink: string
	}
}

type State = {
	blockBuilder?: HTMLElement
	active: boolean
} & InitProps

type InitProps = {
	onComplete?: () => void
	onEnd?: () => void
	onChange?: () => void
}

const {
	blockBuilder: { selector },
} = module

export const BlockBuilder = () => {
	const { state, setState } = useState<State>({
		active: false,
	})

	const handleActivateBuilder = (el: HTMLElement) => {
		const click = useEvent(el, 'click')
		const { blockBuilder } = state

		if (!blockBuilder) {
			return
		}
		click.register(() => {
			if (state.active) {
				cn.removeClass(blockBuilder, modifier.active)
				setState({ active: false })
			} else {
				cn.addClass(blockBuilder, modifier.active)
				setState({ active: true })
			}
		})
	}

	const handleActivateSection = (el: HTMLElement) => {
		const click = useEvent(el, 'click')
		const { blockBuilder } = state

		if (!blockBuilder) {
			return
		}
		const sections = blockBuilderSelector.getBlockBuilderSections(blockBuilder)
		const sectionsActivators = blockBuilderSelector.getBlockBuilderSectionActivator(blockBuilder)

		if (!sections || !sectionsActivators) {
			return
		}
		const sectionsLength = sections.length
		const sectionsActivatorsLength = sectionsActivators.length

		if (sectionsLength !== sectionsActivatorsLength) {
			// eslint-disable-next-line no-console
			console.warn('BLOCK BUILDER: Sections and sections activators lengths are not equal!')
			return
		}
		click.register(() => {
			const id = el.getAttribute(selector.sectionActivator)

			if (!id) {
				return
			}
			const section = blockBuilderSelector.getBlockBuilderSectionByValue(blockBuilder, id)

			if (!section) {
				return
			}
			for (let i = 0; i < sectionsLength; i++) {
				const s = sections[i]
				const sa = sectionsActivators[i]

				if (!s) {
					continue
				}
				cn.addClass(s, modifier.hidden)
				cn.removeClass(sa, modifier.active)
			}
			cn.removeClass(section, modifier.hidden)
			cn.addClass(el, modifier.active)
		})
	}

	const show = () => {
		const { blockBuilder } = state

		if (!blockBuilder) {
			return
		}
		cn.removeClass(blockBuilder, modifier.hidden)

		const flipper = blockBuilderSelector.getBlockBuilderFlipper()
		if (flipper) {
			cn.addClass(flipper, 'hidden')
		}
	}

	const hide = () => {
		const { blockBuilder } = state

		if (!blockBuilder) {
			return
		}
		cn.addClass(blockBuilder, modifier.hidden)

		const flipper = blockBuilderSelector.getBlockBuilderFlipper()
		if (flipper) {
			cn.removeClass(flipper, 'hidden')
		}
	}

	const initSectionActivator = () => {
		const { blockBuilder } = state

		if (!blockBuilder) {
			return
		}
		const activators = blockBuilderSelector.getBlockBuilderSectionActivator(blockBuilder)

		if (!activators) {
			return
		}
		const { length } = activators

		if (length === 0) {
			return
		}
		for (let i = 0; i < length; i++) {
			const activator = activators[i]

			if (!activator) {
				return
			}
			handleActivateSection(activator)
		}
	}

	const initSwitcher = () => {
		const { blockBuilder } = state

		if (!blockBuilder) {
			return
		}
		const blockBuilderSwitch = blockBuilderSelector.getBlockBuilderSwitch(blockBuilder)

		if (!blockBuilderSwitch) {
			return
		}
		handleActivateBuilder(blockBuilderSwitch)
	}

	const init = (props?: InitProps) => {
		if (props) {
			setState({ props })
		}

		setState({ blockBuilder: blockBuilderSelector.getBlockBuilder() })
		initSwitcher()
		initSectionActivator()
	}

	return {
		init,
		show,
		hide,
	}
}
